import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  Account,
  ActionType,
  ContractStatusEnum,
  IPopupModelV2,
  IVerknuepfungen,
  ModalChild,
  VERKNUEPFUNGSSTATUS_VORSCHLAG_ABGELEHNT,
  VERKNUEPFUNGSSTATUS_VORSCHLAG_BESTAETIGT,
} from '@mwe/models';
import { cloneDeepJSON, isArrayWithMinOneItem, isContractStatus, parseVerknuepfung } from '@mwe/utils';
import {
  AccountLogic,
  DsgvoLogic,
  LoggingService,
  PopupService,
  ProductAddStateService,
  ProductLogic,
  ProductService,
  ProductStateService,
} from '@mwe/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HeadlineService } from '../headline/headline.service';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { ProductsGroupComponent } from '../product-group/products-group.component';
import { DsgvoDisclaimerComponent } from '../dsgvo/dsgvo-disclaimer/dsgvo-disclaimer.component';
import { ButtonComponent } from '../buttons/button/button.component';
import { ModalComponent } from '../modal/modal.component';
import { InlineNotificationComponent } from '../inline-notification/inline-notification.component';

@Component({
  selector: 'mwe-association-suggestions',
  templateUrl: './association-suggestions.component.html',
  providers: [HeadlineService],
  imports: [
    TranslateDirective,
    ProductsGroupComponent,
    DsgvoDisclaimerComponent,
    ButtonComponent,
    ModalComponent,
    InlineNotificationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssociationSuggestionsComponent implements OnInit, ModalChild {
  associationSuggestions = signal<IVerknuepfungen[]>([]);
  currentSuggestionIdx = signal(1);
  totalSuggestions = computed(() => this.associationSuggestions()?.length);
  isSubmittingAdd = signal<boolean>(false);
  isSubmittingIgnore = signal<boolean>(false);
  isError = signal<boolean>(false);
  account = signal<Account>(null);

  popupModel = signal<IPopupModelV2>(null);

  private activeModal = inject(NgbActiveModal);
  private productStateService = inject(ProductStateService);
  private productLogic = inject(ProductLogic);
  currentSuggestions = computed(() => {
    const products = parseVerknuepfung(this.associationSuggestions()[this.currentSuggestionIdx() - 1], true);
    return this.productLogic.orderAndGroupByAddresses(products);
  });
  private popupService = inject(PopupService);
  private productService = inject(ProductService);
  private loggingService = inject(LoggingService);
  private accountLogic = inject(AccountLogic);
  private productAddStateService = inject(ProductAddStateService);
  private dsgvoLogic = inject(DsgvoLogic);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    let clonedAssociation = cloneDeepJSON(this.productStateService.associationSuggestions) as IVerknuepfungen[];
    clonedAssociation = clonedAssociation.reduce((suggestions: IVerknuepfungen[], currentValue: IVerknuepfungen) => {
      const activeProducts = currentValue.produkte.filter(p => isContractStatus(p.vertragsStatus, ContractStatusEnum.Aktiv));
      if (isArrayWithMinOneItem(activeProducts)) {
        currentValue.produkte = activeProducts;
        return [...suggestions, currentValue];
      } else {
        return suggestions;
      }
    }, []);
    this.associationSuggestions.set(clonedAssociation);
    this.account.set(this.accountLogic.getAccount());
  }

  closePopup() {
    this.activeModal.close('suggestions-popup-closed');
  }

  async addSuggestion() {
    this.isError.set(false);
    this.isSubmittingAdd.set(true);

    const currentAssociationSuggestion = this.associationSuggestions()?.[this.currentSuggestionIdx() - 1];
    const products = parseVerknuepfung(currentAssociationSuggestion, true);
    this.productAddStateService.addressGroups = this.productLogic.orderAndGroupByAddresses(products);

    try {
      await this.productService.addProducts(
        currentAssociationSuggestion.geschaeftsPartnerId,
        currentAssociationSuggestion.systemId,
        VERKNUEPFUNGSSTATUS_VORSCHLAG_BESTAETIGT,
      );

      await this.dsgvoLogic.triggerDsgvoForAssociations(
        currentAssociationSuggestion.geschaeftsPartnerId,
        currentAssociationSuggestion.systemId,
      );

      this.closePopup();
      this.popupService
        .showAddAssociationConfirmationPopup(false, 'products.confirm.popup.buttonOk')
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(async result => {
            this.loggingService.logAction({
              type: ActionType.ASSOCIATIONS,
              message: `[association-suggestion] added (${this.account().id} / ${this.account().email} / ${currentAssociationSuggestion.geschaeftsPartnerId})`,
            });
            this.isSubmittingAdd.set(false);
            if (result === 'product-choose-confirmation' || result === 'suggestions-popup-closed') {
              this.reloadProductsOverview();
            } else {
              this.productStateService.clearProducts();
              this.removeCurrentSuggestionFromCache();
            }
          }),
        )
        .subscribe();
    } catch (e) {
      this.isError.set(true);
      this.isSubmittingAdd.set(false);
    }
  }

  async ignoreSuggestion() {
    this.isError.set(false);
    this.isSubmittingIgnore.set(true);

    const currentAssociationSuggestion = this.associationSuggestions()[this.currentSuggestionIdx() - 1];
    const products = parseVerknuepfung(currentAssociationSuggestion, true);
    this.productAddStateService.addressGroups = this.productLogic.orderAndGroupByAddresses(products);

    try {
      await this.productService.addProducts(
        currentAssociationSuggestion.geschaeftsPartnerId,
        currentAssociationSuggestion.systemId,
        VERKNUEPFUNGSSTATUS_VORSCHLAG_ABGELEHNT,
        false,
      );

      this.loggingService.logAction({
        type: ActionType.ASSOCIATIONS,
        message: `[association-suggestion] declined (${this.account().id} / ${this.account().email} / ${currentAssociationSuggestion.geschaeftsPartnerId})`,
      });

      this.removeCurrentSuggestionFromCache();
      this.isSubmittingIgnore.set(false);
      this.activeModal.close('suggestion-declined');
      this.reloadProductsOverview();
    } catch (e) {
      this.isError.set(true);
      this.isSubmittingIgnore.set(false);
    }
  }

  removeCurrentSuggestionFromCache() {
    this.productStateService.associationSuggestions = this.productStateService.associationSuggestions?.toSpliced(0, 1);
  }

  private reloadProductsOverview() {
    this.productLogic.clearAssociationsUpdate();
    this.productStateService.clearProducts();
    this.productLogic.refreshProducts().subscribe();
  }
}
